import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  FormControlLabel,
  Checkbox,
  Typography,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { FC, useState, useEffect } from 'react';
import { useJobseekerAddEducationMutation, useJobseekerUpdateEducationMutation } from '@carcareers/store';
import { useAuth } from '@carcareers/hooks';
import { JobseekerEducation } from '@libs/models';

interface JobseekerEducationModalProps {
  type: 'add' | 'edit';
  open: boolean;
  onClose: () => void;
  education?: JobseekerEducation;
}

export const JobseekerEducationModal: FC<JobseekerEducationModalProps> = ({ type, open, onClose, education }) => {
  const { session } = useAuth();
  const [addEducation, { isLoading: isAdding }] = useJobseekerAddEducationMutation();
  const [updateEducation, { isLoading: isUpdating }] = useJobseekerUpdateEducationMutation();

  const [formData, setFormData] = useState({
    school: '',
    level: '',
    areaOfStudy: '',
    description: '',
    current: false,
    startDate: '',
    endDate: '',
  });

  useEffect(() => {
    if (type === 'edit' && education) {
      setFormData({
        school: education.school || '',
        level: education.level || '',
        areaOfStudy: education.areaOfStudy || '',
        description: education.description || '',
        current: education.current || false,
        startDate: education.startDate ? new Date(education.startDate).toISOString().split('T')[0] : '',
        endDate: education.endDate ? new Date(education.endDate).toISOString().split('T')[0] : '',
      });
    } else if (type === 'add') {
      setFormData({
        school: '',
        level: '',
        areaOfStudy: '',
        description: '',
        current: false,
        startDate: '',
        endDate: '',
      });
    }
  }, [type, education, open]);

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!session?.profile?.id) return;

    try {
      if (type === 'add') {
        await addEducation({
          jobseekerId: session.profile.id,
          school: formData.school,
          level: formData.level,
          areaOfStudy: formData.areaOfStudy,
          description: formData.description,
          current: formData.current,
          startDate: new Date(formData.startDate),
          endDate: formData.current || !formData.endDate ? undefined : new Date(formData.endDate),
        }).unwrap();
      } else if (type === 'edit' && education) {
        await updateEducation({
          id: Number(education.id),
          jobseekerId: Number(session.profile.id),
          school: formData.school,
          level: formData.level,
          areaOfStudy: formData.areaOfStudy,
          description: formData.description,
          current: formData.current,
          startDate: new Date(formData.startDate),
          endDate: formData.current || !formData.endDate ? undefined : new Date(formData.endDate),
        }).unwrap();
      }

      if (type === 'add') {
        setFormData({
          school: '',
          level: '',
          areaOfStudy: '',
          description: '',
          current: false,
          startDate: '',
          endDate: '',
        });
      }
      onClose();
    } catch (error) {
      console.error(`Error ${type === 'add' ? 'adding' : 'updating'} education:`, error);
    }
  };

  const isFormValid = formData.school && formData.level && formData.areaOfStudy && formData.description && formData.startDate;
  const isLoading = isAdding || isUpdating;

  const isDateValid = formData.current || !formData.endDate || !formData.startDate || new Date(formData.endDate) > new Date(formData.startDate);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: 4,
          },
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">{type === 'add' ? 'Add Education' : 'Edit Education'}</Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          <TextField
            label="School"
            value={formData.school}
            onChange={(e) => handleInputChange('school', e.target.value)}
            fullWidth
            required
            sx={{ borderRadius: 4 }}
            InputProps={{
              sx: { borderRadius: 4 },
            }}
          />
          <TextField
            label="Level"
            value={formData.level}
            onChange={(e) => handleInputChange('level', e.target.value)}
            fullWidth
            required
            placeholder="e.g., Bachelor's, Master's, PhD, Associate's"
            sx={{ borderRadius: 4 }}
            InputProps={{
              sx: { borderRadius: 4 },
            }}
          />
          <TextField
            label="Area of Study"
            value={formData.areaOfStudy}
            onChange={(e) => handleInputChange('areaOfStudy', e.target.value)}
            fullWidth
            required
            placeholder="e.g., Computer Science, Business Administration"
            sx={{ borderRadius: 4 }}
            InputProps={{
              sx: { borderRadius: 4 },
            }}
          />
          <TextField
            label="Description"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            fullWidth
            multiline
            rows={4}
            required
            placeholder="Describe your education, achievements, relevant coursework, etc."
            sx={{ borderRadius: 4 }}
            InputProps={{
              sx: { borderRadius: 4 },
            }}
          />
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
            <TextField
              label="Start Date"
              type="date"
              value={formData.startDate}
              onChange={(e) => handleInputChange('startDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              slotProps={{
                htmlInput: {
                  shrink: true,
                },
                input: {
                  sx: { borderRadius: 4 },
                },
              }}
              fullWidth
              required
              sx={{ borderRadius: 4 }}
            />

            {!formData.current && (
              <TextField
                label="End Date"
                type="date"
                value={formData.endDate}
                onChange={(e) => handleInputChange('endDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
                slotProps={{
                  htmlInput: {
                    shrink: true,
                  },
                  input: {
                    sx: { borderRadius: 4 },
                  },
                }}
                fullWidth
                sx={{ borderRadius: 4 }}
                error={!isDateValid && !!formData.endDate && !!formData.startDate}
                helperText={!isDateValid && !!formData.endDate && !!formData.startDate ? 'End date must be after start date' : ''}
              />
            )}
          </Box>
          <FormControlLabel
            control={
              <Checkbox checked={formData.current} onChange={(e) => handleInputChange('current', e.target.checked)} />
            }
            label="I am currently studying here"
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} variant="outlined" sx={{ borderRadius: 2 }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isFormValid || !isDateValid || isLoading}
          sx={{ borderRadius: 2 }}
        >
          {isLoading
            ? type === 'add'
              ? 'Adding...'
              : 'Updating...'
            : type === 'add'
            ? 'Add Education'
            : 'Update Education'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 